/**
 * Singleton fetcher for `GET /badge/count`.
 *
 * `/badge/count` is a heavy endpoint (~1-3s) that gets triggered from many
 * places: the app-mount effect in `App.js`, every `Coversation` /
 * `GroupConversation` mount, several socket-event handlers, and
 * `ProjectNotification`. Multiple unrelated UI events frequently land in the
 * same tick and each one used to fire its own request, fighting for the
 * browser's per-host connection slots. In dev, React.StrictMode double-runs
 * every effect, so the wasted request count doubles again.
 *
 * This helper does two things:
 *
 *  1. In-flight dedupe — if a request is already pending, every subsequent
 *     caller within the same window receives the same Promise. The BE only
 *     gets one hit; every caller still resolves with the response.
 *  2. Short post-flight reuse — for `BADGE_COUNT_REUSE_MS` after a successful
 *     response, callers reuse the cached payload instead of re-fetching.
 *     This catches bursts that arrive a few ms after the previous request
 *     resolved (e.g. a socket event right after a screen mount), without
 *     making the count visibly stale: socket-driven mutations are still
 *     applied directly to the Redux `unSeenAllCount` slice in `App.js`.
 *
 * Exposes `getBadgeCount()` (preferred) and `invalidateBadgeCount()` for
 * call sites that just performed an action where the cached payload is
 * known to be wrong (e.g. immediate logout, mark-all-read).
 *
 * `force: true` semantics (important for instant counters):
 *  - Bumps an internal generation so any in-flight request started BEFORE
 *    the mutation resolves as `{ data: { status: false, stale: true } }`
 *    instead of applying a pre-mutation snapshot over optimistic UI updates.
 *  - Chains a fresh fetch after the current in-flight settles so the forced
 *    caller always gets a post-mutation `/badge/count`.
 */

import { callAPI } from "./apiUtils";

const BADGE_COUNT_PATH = "/badge/count";
// How long to reuse a successful response across multiple callers. Kept
// short so any genuinely new event triggers a refresh quickly; bursts of
// "all things mounting at once" (the common case) still collapse to one
// network call.
const BADGE_COUNT_REUSE_MS = 750;

let inFlight = null;
/**
 * A forced fetch queued behind the current in-flight request whose own
 * network call has not started yet. Further forces join it (it will already
 * see their mutation) instead of chaining onto it — chaining a second force
 * onto the first and letting the first resolve to "the latest inFlight"
 * made the two promises wait on each other forever.
 */
let pendingChain = null;
let lastResponse = null;
let lastResponseAt = 0;
/** Bumped on every `force: true` so pre-mutation in-flight results are dropped. */
let generation = 0;

function startFetch(gen) {
  const p = (async () => {
    try {
      const response = await callAPI(BADGE_COUNT_PATH, {}, "GET");
      // A forced refresh started after us — do not let this snapshot land in
      // cache or Redux (callers gate on `data.status`).
      if (gen !== generation) {
        return { data: { status: false, stale: true }, status: 200 };
      }
      lastResponse = response;
      lastResponseAt = Date.now();
      return response;
    } finally {
      if (inFlight === p) inFlight = null;
    }
  })();
  inFlight = p;
  return p;
}

/**
 * Returns the same response shape `callAPI('/badge/count', ...)` would,
 * deduped across concurrent callers.
 *
 * Pass `{ force: true }` to bypass the post-flight reuse window when you
 * KNOW the BE state has just changed (e.g. you're calling this inside a
 * socket-event handler that signalled a new message arrived). Without
 * `force`, a still-warm cached response from before the mutation will be
 * served and overwrite any in-memory increment that was already dispatched
 * — the user-visible symptom is "counter doesn't update on new emails".
 *
 * With `force: true`, any in-flight request started before the force is
 * treated as stale (its waiters see `data.status === false`) and a fresh
 * request is chained so the forced caller never adopts a pre-mutation
 * snapshot.
 */
export function getBadgeCount(options) {
  const force = !!(options && options.force);
  const now = Date.now();

  if (force) {
    lastResponse = null;
    lastResponseAt = 0;
    generation += 1;
  }

  if (inFlight) {
    if (!force) return inFlight;
    // A queued fetch that hasn't hit the network yet is already post-mutation.
    if (pendingChain) return pendingChain;
    // Chain a fresh fetch after the current one settles.
    const prev = inFlight;
    const chained = prev
      .catch(() => null)
      .then(() => {
        pendingChain = null;
        return startFetch(generation);
      });
    pendingChain = chained;
    inFlight = chained;
    return chained;
  }

  if (!force && lastResponse && now - lastResponseAt < BADGE_COUNT_REUSE_MS) {
    return Promise.resolve(lastResponse);
  }

  return startFetch(generation);
}

/**
 * Drop the cached payload so the next `getBadgeCount()` call hits the
 * backend immediately. Use after explicit user actions (logout, mark all
 * as read) where the previous response is known to be stale.
 */
export function invalidateBadgeCount() {
  lastResponse = null;
  lastResponseAt = 0;
  // Drop in-flight /badge/count snapshots so they cannot overwrite an
  // optimistic decrement that just happened (e.g. opening a briefing).
  generation += 1;
}

/**
 * Subtract a just-opened chat's unread from the in-memory `/badge/count`
 * snapshot so filter-tab / SideNav badges drop on click instead of waiting
 * for subscribe + GET /badge/count (~1-3s). Callers still force-fetch
 * afterwards to reconcile with the backend.
 */
export function applyOptimisticChatRead(unSeenAllCount, { isOneToOne, isInternal, unreadCount } = {}) {
  const unread = Number(unreadCount) || 0;
  if (unread <= 0) return unSeenAllCount;
  const next = { ...(unSeenAllCount || {}) };
  if (isOneToOne) {
    next.unseenChatCount = Math.max(0, (Number(next.unseenChatCount) || 0) - unread);
  } else if (isInternal) {
    next.unseenInternalProject = Math.max(0, (Number(next.unseenInternalProject) || 0) - unread);
  } else {
    next.unseenExternalProject = Math.max(0, (Number(next.unseenExternalProject) || 0) - unread);
  }
  return next;
}
